import { Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import SchoolFeaturedCard from '../../ResourcesPage/SchoolsPage/SchoolCard';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import EmptyState from '../../shared/EmptyState';
import { getUserId } from '../../shared/getToken';
import { tokenUpdate } from '../../../atoms/authModalControl';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { getAllReadingHistory } from '../../../services/School';
import { User } from '../../../utils/interfaces/User';

const Schools = () => {
  const [recentReadSchool, setRecentReadSchool] = useState<
    { image: string; desc: string; chapterNum: number; chaptersRead: number }[]
  >([]);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [recentSchoolLoading, setRecentSchoolLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const router = useRouter();
  const screens = useBreakpoint();
  const { search: keyWord, tab } = router.query;
  const userData: User | undefined = useRecoilValue(user);

  const fetchSchools = useCallback(async (keyword?: string) => {
    try {
      setRecentSchoolLoading(true);
      const userId = await getUserId();
      const { data } = await getAllReadingHistory({
        id: userId as string,
        keyword,
      });
      setRecentReadSchool(
        data?.map((school: any) => ({
          id: school.id,
          desc: school.name,
          chapterNum: school.posts_count,
          image: school.documents.file_path,
          chaptersRead: school?.post_read_count,
          slug: school?.slug,
        })) || [],
      );
    } catch (error) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error fetching reading history:', error);
      }
    } finally {
      setRecentSchoolLoading(false);
    }
  }, []);

  const handleSearch = useCallback(
    debounce((keyword: string) => {
      fetchSchools(keyword);
      const eventParams: EventDetails = {
        event_name: 'search_keyword',
        source: 'library',
        source_type: 'search',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'string',
        value: keyword,
      };
      if (keyWord) {
        logEvent({
          eventDetails: eventParams,
          userData,
          pathname: router.pathname,
        });
      }
    }, 500),
    [fetchSchools, keyWord, userData, router.pathname],
  );

  useEffect(() => {
    if (searchKeyword) {
      handleSearch(searchKeyword);
    } else {
      fetchSchools();
    }
    return () => handleSearch.cancel();
  }, [searchKeyword, fetchSchools]);

  useEffect(() => {
    if (tab === '2') {
      setSearchKeyword(keyWord as string);
    }
  }, [keyWord, tab]);

  useEffect(() => {
    if (isTokenChanged) {
      fetchSchools();
      setIsTokenChanged(false);
    }
  }, [isTokenChanged, fetchSchools]);

  return (
    <div className='relative overflow-hidden'>
      <div className='flex justify-center xs:justify-start items-center'>
        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4'>
          {!recentSchoolLoading
            ? recentReadSchool.map((item, index) => (
                <span
                  className='group cursor-pointer p-1 lg:p-2 no-select'
                  key={`${item?.desc}-${index}`}
                >
                  <SchoolFeaturedCard item={item} isGrid={true} />
                </span>
              ))
            : Array.from({ length: 10 }).map((_, index) => (
                <span
                  key={index}
                  className='group cursor-pointer p-1 md:p-1.5 bg-transparent no-select'
                >
                  <Skeleton.Button
                    style={{
                      width: screens.xl ? '200px' : '150px',
                      height: screens.xl ? '200px' : '150px',
                    }}
                    active={true}
                    className='rounded-sm md:rounded-md transition-transform transform hover:scale-[1.03] duration-500 max-w-[150px] lg:max-w-[140px] xl:max-w-[220px]'
                  />
                </span>
              ))}
        </div>
        <ShouldRender check={!recentReadSchool.length && !recentSchoolLoading}>
          <div className='flex w-full justify-center items-center'>
            <EmptyState
              heading='No Schools right now'
              link='/schools/'
              desc='Your recent read schools will be visible here'
            />
          </div>
        </ShouldRender>
      </div>
    </div>
  );
};

export default Schools;
